import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  SafeAreaView,
  StyleSheet,
  Text,
  TextInput,
  View,
  Pressable,
} from "react-native";
import SearchBusinessCard from "@/components/search/SearchBusinessCard";
import { searchBusinesses } from "@/lib/search/searchBusinesses";
import type { BusinessSearchDoc } from "@/lib/search/businessSearch";

export default function SearchScreen() {
  const [queryText, setQueryText] = useState("");
  const [countyId, setCountyId] = useState("");
  const [cityId, setCityId] = useState("");
  const [areaId, setAreaId] = useState("");

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BusinessSearchDoc[]>([]);

  async function runSearch() {
    const q = queryText.trim();
    if (!q) {
      Alert.alert("Search", "Type a search word like Cake.");
      return;
    }

    try {
      setLoading(true);

      const rows = await searchBusinesses({
        queryText: q,
        location: {
          countyId: countyId.trim() || null,
          cityId: cityId.trim() || null,
          areaId: areaId.trim() || null,
        },
      });

      setResults(rows);
    } catch {
      console.warn("Business search failed.");
      Alert.alert(
        "Search Error",
        "Could not complete search. Check Firestore indexes and try again."
      );
    } finally {
      setLoading(false);
    }
  }

  function clearFilters() {
    setCountyId("");
    setCityId("");
    setAreaId("");
  }

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.container}>
        <Text style={styles.title}>Search Businesses</Text>
        <Text style={styles.subtitle}>
          Search by business name, category, subcategory, product, or service.
        </Text>

        <TextInput
          value={queryText}
          onChangeText={setQueryText}
          placeholder="Search e.g. Cake"
          style={styles.input}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
          onSubmitEditing={runSearch}
        />

        <Text style={styles.sectionTitle}>Location filters</Text>

        <TextInput
          value={countyId}
          onChangeText={setCountyId}
          placeholder="County ID (optional)"
          style={styles.input}
          autoCapitalize="none"
        />

        <TextInput
          value={cityId}
          onChangeText={setCityId}
          placeholder="City ID (optional)"
          style={styles.input}
          autoCapitalize="none"
        />

        <TextInput
          value={areaId}
          onChangeText={setAreaId}
          placeholder="Area ID (optional)"
          style={styles.input}
          autoCapitalize="none"
        />

        <View style={styles.buttonRow}>
          <Pressable style={styles.searchButton} onPress={runSearch}>
            <Text style={styles.searchButtonText}>Search</Text>
          </Pressable>

          <Pressable style={styles.clearButton} onPress={clearFilters}>
            <Text style={styles.clearButtonText}>Clear Location</Text>
          </Pressable>
        </View>

        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator />
            <Text style={styles.loadingText}>Searching...</Text>
          </View>
        ) : (
          <FlatList
            data={results}
            keyExtractor={(item) => item.businessId}
            renderItem={({ item }) => <SearchBusinessCard item={item} />}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={
              <View style={styles.emptyWrap}>
                <Text style={styles.emptyTitle}>No results yet</Text>
                <Text style={styles.emptyText}>
                  Search for a word like Cake, Salon, Repair, Hardware.
                </Text>
              </View>
            }
          />
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: "#f9fafb",
  },
  container: {
    flex: 1,
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: "800",
    color: "#111827",
  },
  subtitle: {
    marginTop: 6,
    marginBottom: 16,
    fontSize: 14,
    lineHeight: 20,
    color: "#4b5563",
  },
  sectionTitle: {
    marginTop: 6,
    marginBottom: 8,
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 14,
  },
  searchButton: {
    flex: 1,
    height: 46,
    borderRadius: 12,
    backgroundColor: "#111827",
    alignItems: "center",
    justifyContent: "center",
  },
  searchButtonText: {
    color: "#fff",
    fontWeight: "700",
  },
  clearButton: {
    flex: 1,
    height: 46,
    borderRadius: 12,
    backgroundColor: "#e5e7eb",
    alignItems: "center",
    justifyContent: "center",
  },
  clearButtonText: {
    color: "#111827",
    fontWeight: "700",
  },
  loadingWrap: {
    marginTop: 20,
    alignItems: "center",
    gap: 8,
  },
  loadingText: {
    color: "#4b5563",
  },
  listContent: {
    paddingTop: 8,
    paddingBottom: 40,
  },
  emptyWrap: {
    paddingTop: 28,
    alignItems: "center",
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
    color: "#6b7280",
    textAlign: "center",
  },
});
